import { Menu, Portal, Button, Input, Grid, Box, Flex, Text, GridItem } from '@chakra-ui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { MenuProductItemFragment, MenuSectionItemFragment } from '@app/graphql/types/graphql.ts';
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { naturalCompare } from '@app/utils/natural-compare.ts';
import { ArrowsUpDownIcon, CheckCircleIcon } from '@heroicons/react/16/solid';
import { SortType } from '@app/page/online-order/product-picker/utils/sort-type.ts';

const sortTypeList: SortType[] = [SortType.INCREASE, SortType.DECREASE, SortType.NATURAL];

export default function FunctionalBar(props: {
  sections: readonly MenuSectionItemFragment[];
  products: readonly MenuProductItemFragment[];
  searchProduct: (searchString: string) => void;
  setSortType: (sortType: SortType) => void;
  sortType: SortType;
}) {
  const sections = [...props.sections].sort((a, b) => naturalCompare(a.code, b.code));
  const { t } = useTranslate();
  const [currentSection, setCurrentSection] = useState<string>(
    t('online-order.list.header.category.placeholder', 'Category')
  );
  const [expandSearch, setExpandSearch] = useState<boolean>(false);

  return (
    <Grid
      className="functional-bar"
      templateColumns={{ base: '1fr', md: '1fr 2fr' }}
      gap={2}
      borderRadius="xl"
      borderBottomRadius="none"
      borderBottomWidth="1px"
      p={4}
    >
      {/* Category Menu */}
      <GridItem className="functional-bar__filters">
        <Menu.Root>
          <Menu.Trigger asChild>
            <Button
              className="functional-bar__category-select"
              variant="outline"
              w="full"
              justifyContent="space-between"
              px={3}
              py={2.5}
              fontSize="sm"
            >
              <Flex w="full" justify="space-between" align="center">
                <Text>{currentSection}</Text>
                <ChevronUpDownIcon style={{ width: '20px', height: '20px' }} />
              </Flex>
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content
                className="functional-bar__category-menu"
                maxH="96"
                w="full"
                borderRadius="2xl"
                overflowY="auto"
                zIndex={20}
              >
                {sections.map((section, index) => {
                  const products = props.products.filter((product) => product.sectionId == section.id);
                  if (products.length == 0) {
                    return null;
                  }
                  const filteredSections = sections.filter((s) => props.products.some((p) => p.sectionId === s.id));
                  const isLastItem = index === filteredSections.length - 1;

                  return (
                    <Menu.Item
                      key={section.id}
                      value={section.id}
                      onClick={() => {
                        setCurrentSection(section.name);
                        // Navigate to section
                        const element = document.getElementById(section.id);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                    >
                      <Box
                        className="functional-bar__category-item"
                        px={4}
                        py={3}
                        fontSize="sm"
                        maxW={80}
                        overflow="wrap"
                        whiteSpace="normal"
                        wordBreak="break-word"
                        lineHeight="1.4"
                        borderBottom={!isLastItem ? '1px solid' : 'none'}
                        borderBottomColor={!isLastItem ? 'var(--template-border)' : 'transparent'}
                        w="full"
                      >
                        {section.name}
                      </Box>
                    </Menu.Item>
                  );
                })}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </GridItem>
      {/* Search and Sort Section */}
      <GridItem className="functional-bar__search">
        <Flex justify="space-between" gap={2}>
          <Flex w={expandSearch ? 'full' : 'auto'} flex={expandSearch ? '1' : '1'} align="center" gap={2}>
            <Input
              className="functional-bar__search-input"
              type="search"
              placeholder={t('online-order.list.header.search.placeholder', 'Search')}
              fontSize="sm"
              onClick={() => {
                if (typeof window !== 'undefined' && window.innerWidth < 640) {
                  setExpandSearch(true);
                }
              }}
              onChange={(e) => {
                props.searchProduct(e.target.value);
              }}
            />

            {expandSearch && (
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color="gray.700"
                _hover={{ color: 'gray.600' }}
                cursor="pointer"
                onClick={() => {
                  setExpandSearch(false);
                }}
              >
                {t('online-order.list.header.search.button.cancel', 'Cancel')}
              </Text>
            )}
          </Flex>

          {/* Sort Menu */}
          <Box display={expandSearch ? 'none' : 'block'}>
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button
                  className="functional-bar__sort-btn"
                  variant="outline"
                  w="auto"
                  px={3}
                  py={2.5}
                  fontSize="sm"
                >
                  <Flex align="center" gap={2}>
                    <ArrowsUpDownIcon style={{ width: '24px', height: '24px' }} />
                    <Text>{t('online-order.picked-list.sort-button', 'Sort')}</Text>
                  </Flex>
                </Button>
              </Menu.Trigger>
              <Portal>
                <Menu.Positioner>
                  <Menu.Content
                    className="functional-bar__sort-menu"
                    maxH="96"
                    borderRadius="2xl"
                    overflowY="auto"
                    zIndex={10}
                  >
                    {sortTypeList.map((sortType, index) => (
                      <Menu.Item
                        key={index}
                        value={sortType}
                        onClick={() => {
                          props.setSortType(sortType);
                        }}
                      >
                        <Flex className="functional-bar__sort-item" align="center" gap={2} px={4} py={3} w="full">
                          <Box w={4} h={4}>
                            {props.sortType === sortType && (
                              <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                            )}
                          </Box>
                          <Text fontSize="sm">{t(`online-order.product-list.sort.${sortType}`)}</Text>
                        </Flex>
                      </Menu.Item>
                    ))}
                  </Menu.Content>
                </Menu.Positioner>
              </Portal>
            </Menu.Root>
          </Box>
        </Flex>
      </GridItem>
    </Grid>
  );
}
